import * as React from 'react';
import type { Editor, Range } from '@tiptap/react';

// --- Tiptap UI ---
import type {
  SuggestionItem,
  SuggestionMenuProps,
  SuggestionMenuRenderProps,
} from '../../tiptap-ui-utils/suggestion-menu';
import { SuggestionMenu } from '../../tiptap-ui-utils/suggestion-menu';

// --- UI Primitives ---
import { ArrowRight, Check, ChevronRight } from 'lucide-react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Card, CardContent } from '@cotera/client/v0/components/ui/card';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  getResourceMentionColorFromMentionData,
  resourceColorDotClassName,
  resourceColorMentionVariant,
} from '@cotera/client/app/v1/resources/type/resource-type-colors';
import {
  createMentionFuzzyFilter,
  fuzzyFilterMentionOptions,
} from './mention-fuzzy-search';

export type MentionColorVariant =
  | 'blue'
  | 'amber'
  | 'purple'
  | 'primary'
  | 'gray'
  | 'orange'
  | 'rose'
  | 'green'
  | 'slate'
  | 'violet'
  | 'teal'
  | 'brown'
  | 'muted'
  | 'yellow'
  | 'cotera'
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline';

export type MentionSubgroup = {
  id: string;
  label: string;
  /**
   * Optional icon (e.g. provider logo) shown next to this subgroup in the Tools
   * cascade providers column.
   */
  icon?: React.ReactNode;
};

/**
 * Human-friendly preview for a mention option, shown in the Tools cascade detail
 * column. Abstracts away raw API/endpoint details (verbs, schemas) into plain
 * language so users can discover tools without knowing exactly what to search for.
 */
export type MentionOptionDetail = {
  /** Small uppercase eyebrow above the title, e.g. "APOLLO · PEOPLE ENRICHMENT". */
  eyebrow?: string;
  /** Human-friendly title, e.g. "People Enrichment". */
  title: string;
  /** One-line, plain-language summary of what this does. */
  summary?: string;
  /** Access descriptor, e.g. { label: 'Read-only', tone: 'read' }. */
  access?: { label: string; tone: 'read' | 'write' };
  /**
   * When set, an "Open details →" link is shown; clicking it runs this (e.g.
   * open the tool connections modal for this tool) and dismisses the menu.
   */
  onOpen?: () => void;
};

export type MentionGroup = {
  id: string;
  label: string;
  subgroups?: MentionSubgroup[];
  /** Optional icon (e.g. provider logo) shown next to the group header or submenu trigger. */
  icon?: React.ReactNode;
};

export type MentionOption<T> = {
  label: string;
  data: T;
  color?: MentionColorVariant;
  referenceId?: string;
  group?: string;
  subgroup?: string;
  /** Shown after the name in picker rows (e.g. "3 steps"). */
  badge?: string;
  /** Shown after the name in compact rows (e.g. search "Suggested" list). */
  typeLabel?: string;
  /**
   * When true, a check is shown on the row in the Tools cascade tools column
   * (e.g. a tool that is connected / ready to use).
   */
  available?: boolean;
  /** Rich, plain-language preview shown in the Tools cascade detail column. */
  detail?: MentionOptionDetail;
};

/**
 * Group ids in this order are shown first in the menu. Extend to pin other groups or reorder.
 * Example: ['agents', 'github'] puts Your Agents first, then GitHub, then remaining groups.
 */
/** Coco Inputs first when present, then Your Agents, then Agents provider, then other groups. */
const DEFAULT_GROUP_PRIORITY: string[] = [
  'coco-input',
  'your-agents',
  'agents',
];

type MentionDropdownMenuProps<T> = Omit<
  SuggestionMenuProps,
  'items' | 'children'
> & {
  options: MentionOption<T>[];
  /** Define groups with their display order. Items are sorted by group order, then alphabetically within groups. */
  groups?: MentionGroup[];
  /** Group ids to show first, in order. Groups not listed follow in their default (groups) order. Default: ['agents'] so "Your Agents" is always at top. */
  groupPriority?: string[];
  /** When true, shows a loading indicator in the popover (e.g. while tools are loading). */
  isLoading?: boolean;
  /** Maximum items per group before showing "X more". Unused quota from smaller groups flows to larger ones. Default: 10 */
  maxPerGroup?: number;
  filterOptions?: (
    options: MentionOption<T>[],
    query: string
  ) => MentionOption<T>[];
  renderOption?: (option: MentionOption<T>) => React.ReactNode;
  getOptionId?: (option: MentionOption<T>) => string;
  getOptionLabel?: (option: MentionOption<T>) => string;
  /**
   * When this returns true, the default mention insertion is skipped (caller handled it,
   * e.g. opened a dialog for Coco input mentions).
   */
  interceptMentionInsert?: (props: {
    editor: Editor;
    range: Range;
    context?: MentionOption<T>;
  }) => boolean;
};

interface MentionItemProps<T> {
  item: SuggestionItem<MentionOption<T>>;
  isSelected: boolean;
  onSelect: () => void;
  renderOption?: (option: MentionOption<T>) => React.ReactNode;
  /**
   * Tools cascade tools column: show a check for ready-to-use options and hide
   * the redundant "Tool" type label (the column header already says "Tools").
   */
  showAvailability?: boolean;
  /** Fired on hover; used to preview the option in the detail column. */
  onHover?: () => void;
  /**
   * Render a taller row (used in the Tools cascade / submenu panels) so options
   * are easier to hit and track with the cursor while navigating between columns.
   */
  tall?: boolean;
}

/** When there is no search and total options are at least this many, show each group as a submenu; otherwise show all inline. */
const SUBMENU_TOTAL_THRESHOLD = 10;

/** When user is searching, show this many most relevant results ungrouped at top; the rest are grouped. */
const SEARCH_TOP_RESULTS_COUNT = 5;

type GroupedSuggestionItem<T> = SuggestionItem<MentionOption<T>> & {
  groupId?: string;
  subgroupId?: string;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  isFirstInSubgroup?: boolean;
  overflowCount?: number;
  /** When true, this row opens a sub-menu with subMenuOptions instead of inserting. */
  isSubMenuTrigger?: boolean;
  subMenuGroupId?: string;
  subMenuLabel?: string;
  subMenuOptions?: MentionOption<T>[];
};

/**
 * Derive the dot color from mention type when a color is not explicitly set.
 *
 * Keep this in sync with `getMentionColorFromType` in mention.view.tsx so the menu
 * dot always matches the pill it inserts. One color per category: tools=tool
 * resource color, documents=blue, agents=violet, columns=amber. Folders (green)
 * and triggers (amber) are resources, colored via RESOURCE_TYPE_COLOR_CONFIG in
 * resource-type-colors.ts.
 */
const getColorFromMentionType = (
  t?: string
): MentionColorVariant | undefined => {
  if (t === undefined) {
    return undefined;
  }
  if (t === 'tool') {
    return resourceColorMentionVariant('tool');
  }
  if (t === 'document') {
    return 'blue';
  }
  if (t === 'expr') {
    return 'green';
  }
  if (t === 'agent') {
    return 'violet';
  }
  if (t === 'column') {
    return 'amber';
  }
  if (t === 'resource') {
    return 'primary';
  }
  if (t === 'input') {
    return 'green';
  }
  return 'purple';
};

const colorVariantClasses: Record<MentionColorVariant, string> = {
  blue: 'bg-blue-500',
  amber: 'bg-amber-500',
  purple: 'bg-mention-purple',
  primary: 'bg-primary',
  gray: 'bg-gray-700',
  orange: 'bg-orange-500',
  rose: 'bg-rose-500',
  brown: resourceColorDotClassName('tool'),
  green: 'bg-emerald-500',
  slate: 'bg-slate-500',
  violet: 'bg-violet-500',
  teal: 'bg-teal-500',
  muted: 'bg-gray-700',
  yellow: 'bg-yellow-500',
  cotera: 'bg-indigo-500',
  default: 'bg-primary',
  secondary: 'bg-secondary',
  destructive: 'bg-destructive',
  outline: 'bg-foreground',
};

const defaultFilterOptions = <T,>(
  options: MentionOption<T>[],
  query: string
): MentionOption<T>[] => {
  return fuzzyFilterMentionOptions(options, query);
};

/** Row title in lists; insertion still uses `option.label` unless `getOptionLabel` is set. */
const mentionOptionRowTitle = <T,>(option: MentionOption<T>): string => {
  if (option.typeLabel !== undefined && option.typeLabel !== '') {
    return `${option.label} · ${option.typeLabel}`;
  }
  return option.label;
};

type ToolSubmenuSection<T> = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  options: MentionOption<T>[];
};

const TOOL_SUBMENU_UNGROUPED_ID = '__ungrouped__';

/**
 * Partitions tool mention options by `subgroup` (provider / your-agents), ordered like the
 * Tools group's `subgroups` definition from the caller.
 */
const buildToolSubmenuSections = <T,>(
  toolOptions: MentionOption<T>[],
  toolGroup: MentionGroup | undefined,
  subgroupsMap: Map<string, MentionSubgroup> | undefined
): ToolSubmenuSection<T>[] => {
  const bySubgroup = new Map<string, MentionOption<T>[]>();
  for (const opt of toolOptions) {
    const id = opt.subgroup ?? TOOL_SUBMENU_UNGROUPED_ID;
    const list = bySubgroup.get(id) ?? [];
    list.push(opt);
    bySubgroup.set(id, list);
  }
  const orderedIds: string[] = [];
  if (toolGroup?.subgroups !== undefined) {
    for (const sg of toolGroup.subgroups) {
      if (bySubgroup.has(sg.id)) {
        orderedIds.push(sg.id);
      }
    }
  }
  for (const id of [...bySubgroup.keys()].sort((a, b) => a.localeCompare(b))) {
    if (!orderedIds.includes(id)) {
      orderedIds.push(id);
    }
  }
  const sections: ToolSubmenuSection<T>[] = [];
  for (const id of orderedIds) {
    const opts = bySubgroup.get(id);
    if (opts === undefined || opts.length === 0) {
      continue;
    }
    const subgroup =
      toolGroup?.subgroups?.find((sg) => sg.id === id) ?? subgroupsMap?.get(id);
    const label = subgroup?.label ?? id;
    const icon = subgroup?.icon;
    sections.push({ id, label, icon, options: opts });
  }
  return sections;
};

const flattenToolSubmenuSections = <T,>(
  sections: ToolSubmenuSection<T>[]
): MentionOption<T>[] => sections.flatMap((s) => s.options);

/**
 * Sort and group options by their group, then subgroup, then alphabetically.
 */
const sortAndGroupOptions = <T,>(
  options: MentionOption<T>[],
  groups?: MentionGroup[]
): MentionOption<T>[] => {
  if (!groups || groups.length === 0) {
    // No groups defined, just sort alphabetically
    return [...options].sort((a, b) => a.label.localeCompare(b.label));
  }

  const groupOrder = new Map(groups.map((g, idx) => [g.id, idx]));

  // Build subgroup order map for groups that have subgroups
  const subgroupOrder = new Map<string, Map<string, number>>();
  for (const group of groups) {
    if (group.subgroups) {
      const subgroupMap = new Map(
        group.subgroups.map((sg, idx) => [sg.id, idx])
      );
      subgroupOrder.set(group.id, subgroupMap);
    }
  }

  return [...options].sort((a, b) => {
    const aGroupOrder = a.group
      ? groupOrder.get(a.group) ?? Infinity
      : Infinity;
    const bGroupOrder = b.group
      ? groupOrder.get(b.group) ?? Infinity
      : Infinity;

    // First sort by group order
    if (aGroupOrder !== bGroupOrder) {
      return aGroupOrder - bGroupOrder;
    }

    // Then sort by subgroup within the same group
    if (a.group && a.group === b.group) {
      const groupSubgroupOrder = subgroupOrder.get(a.group);
      if (groupSubgroupOrder) {
        const aSubgroupOrder = a.subgroup
          ? groupSubgroupOrder.get(a.subgroup) ?? Infinity
          : Infinity;
        const bSubgroupOrder = b.subgroup
          ? groupSubgroupOrder.get(b.subgroup) ?? Infinity
          : Infinity;

        if (aSubgroupOrder !== bSubgroupOrder) {
          return aSubgroupOrder - bSubgroupOrder;
        }
      }
    }

    // Then sort alphabetically within the same group/subgroup
    return a.label.localeCompare(b.label);
  });
};

/**
 * Mention dropdown menu component for TipTap editor.
 *
 * This component is decoupled from the editor - it can be passed into the editor
 * rather than being hardcoded. This allows for flexible integration and customization.
 *
 * @example
 * ```tsx
 * // Pass into editor as a UI component
 * const groups: MentionGroup[] = [
 *   { id: 'column', label: 'Columns' },
 *   { id: 'document', label: 'Documents' },
 *   { id: 'tool', label: 'Tools' },
 * ];
 *
 * const options: MentionOption<User>[] = [
 *   {
 *     label: 'John Doe',
 *     data: { id: 1, name: 'John Doe', email: 'john@example.com' },
 *     color: 'blue',
 *     group: 'column'
 *   },
 *   {
 *     label: 'Jane Smith',
 *     data: { id: 2, name: 'Jane Smith', email: 'jane@example.com' },
 *     color: 'green',
 *     group: 'document'
 *   },
 * ];
 *
 * <EditorContentArea>
 *   <MentionDropdownMenu options={options} groups={groups} />
 * </EditorContentArea>
 * ```
 */

export const MentionDropdownMenu = <T,>({
  options,
  groups,
  groupPriority = DEFAULT_GROUP_PRIORITY,
  isLoading = false,
  filterOptions = defaultFilterOptions,
  renderOption,
  getOptionId,
  getOptionLabel,
  interceptMentionInsert,
  ...restProps
}: MentionDropdownMenuProps<T>) => {
  const handleItemSelect = React.useCallback(
    (props: { editor: Editor; range: Range; context?: MentionOption<T> }) => {
      if (
        props.editor == null ||
        props.range == null ||
        props.context == null
      ) {
        return;
      }

      if (interceptMentionInsert?.(props) === true) {
        return;
      }

      const id = getOptionId
        ? getOptionId(props.context)
        : String(props.context.label);
      const label = getOptionLabel
        ? getOptionLabel(props.context)
        : props.context.label;

      props.editor
        .chain()
        .focus()
        .insertContentAt(props.range, [
          {
            type: 'mention',
            attrs: {
              id,
              label,
              referenceId: props.context.referenceId,
              ...(props.context.color !== undefined && {
                color: props.context.color,
              }),
              data: props.context.data
                ? JSON.stringify(props.context.data)
                : undefined,
            },
          },
          {
            type: 'text',
            text: ' ',
          },
        ])
        .run();
    },
    [getOptionId, getOptionLabel, interceptMentionInsert]
  );

  const groupsMap = React.useMemo(() => {
    if (!groups) {
      return undefined;
    }
    return new Map(groups.map((g) => [g.id, g]));
  }, [groups]);

  const subgroupsMap = React.useMemo(() => {
    if (!groups) {
      return undefined;
    }
    const map = new Map<string, MentionSubgroup>();
    for (const group of groups) {
      if (group.subgroups) {
        for (const subgroup of group.subgroups) {
          map.set(subgroup.id, subgroup);
        }
      }
    }
    return map.size > 0 ? map : undefined;
  }, [groups]);

  const defaultFuzzyFilter = React.useMemo(
    () => createMentionFuzzyFilter(options),
    [options]
  );

  const noOpSelect = React.useCallback(
    (_props: { editor: Editor; range: Range; context?: MentionOption<T> }) => {
      // Submenu trigger: selection is handled by opening submenu in the list, not by inserting.
    },
    []
  );

  const buildSuggestionItems = React.useCallback(
    (query: string): GroupedSuggestionItem<T>[] => {
      const filteredOptions =
        filterOptions === defaultFilterOptions
          ? defaultFuzzyFilter(query)
          : filterOptions(options, query);
      if (filteredOptions.length === 0) {
        return [];
      }

      const normalizedQuery = query.trim();
      const hasSearch = normalizedQuery.length > 0;
      const ungroupedKey = '__ungrouped__';
      const groupsMapForLabel = groups
        ? new Map(groups.map((g) => [g.id, g.label]))
        : null;

      function orderedGroupIdsFromMap(
        byGroup: Map<string, MentionOption<T>[]>
      ): string[] {
        const seen = new Set<string>();
        const ids: string[] = [];
        const priority = groupPriority ?? DEFAULT_GROUP_PRIORITY;
        for (const gid of priority) {
          if (byGroup.has(gid) && !seen.has(gid)) {
            seen.add(gid);
            ids.push(gid);
          }
        }
        if (groups) {
          for (const g of groups) {
            if (byGroup.has(g.id) && !seen.has(g.id)) {
              seen.add(g.id);
              ids.push(g.id);
            }
          }
        }
        if (byGroup.has(ungroupedKey) && !seen.has(ungroupedKey)) {
          ids.push(ungroupedKey);
        }
        return ids;
      }

      if (!hasSearch) {
        // No search: group everything by provider (and Your Agents). No duplicate groups.
        const sortedOptions = sortAndGroupOptions(filteredOptions, groups);
        const totalCount = sortedOptions.length;
        const byGroup = new Map<string, MentionOption<T>[]>();
        for (const option of sortedOptions) {
          const gid = option.group ?? ungroupedKey;
          const arr = byGroup.get(gid) ?? [];
          arr.push(option);
          byGroup.set(gid, arr);
        }
        const orderedGroupIds = orderedGroupIdsFromMap(byGroup);

        if (totalCount < SUBMENU_TOTAL_THRESHOLD) {
          // Few results: show all inline, one section per group
          const result: GroupedSuggestionItem<T>[] = [];
          let isFirstGroup = true;
          for (const groupId of orderedGroupIds) {
            const groupOptions = byGroup.get(groupId) ?? [];
            let lastSubgroupId: string | undefined;
            for (let i = 0; i < groupOptions.length; i++) {
              const option = groupOptions[i]!;
              const nextOption = groupOptions[i + 1];
              const isFirstInGroup = isFirstGroup && i === 0;
              const isFirstInSubgroup =
                option.subgroup !== undefined &&
                (isFirstInGroup || option.subgroup !== lastSubgroupId);
              const isLastInGroup = nextOption === undefined;
              result.push({
                title: mentionOptionRowTitle(option),
                subtext: option.label,
                context: option,
                onSelect: handleItemSelect,
                groupId: option.group,
                subgroupId: option.subgroup,
                isFirstInGroup,
                isLastInGroup,
                isFirstInSubgroup,
              });
              lastSubgroupId = option.subgroup;
            }
            isFirstGroup = false;
          }
          return result;
        }

        // Many results: one submenu trigger per group (no top-N, no duplication)
        const result: GroupedSuggestionItem<T>[] = [];
        let isFirstGroup = true;
        for (const groupId of orderedGroupIds) {
          const groupOptions = byGroup.get(groupId) ?? [];
          if (groupOptions.length === 0) {
            continue;
          }
          const groupLabel =
            groupsMapForLabel?.get(groupId) ?? groupId.replace(/-/g, ' ');
          result.push({
            title: groupLabel,
            subtext: groupLabel,
            context: groupOptions[0]!,
            onSelect: noOpSelect,
            groupId,
            isFirstInGroup: isFirstGroup,
            isLastInGroup: true,
            isFirstInSubgroup: false,
            isSubMenuTrigger: true,
            subMenuGroupId: groupId,
            subMenuLabel: groupLabel,
            subMenuOptions: groupOptions,
          });
          isFirstGroup = false;
        }
        return result;
      }

      // Searching: top 5 most relevant ungrouped, then all other results grouped (no duplication)
      const topResults = filteredOptions.slice(0, SEARCH_TOP_RESULTS_COUNT);
      const rest = filteredOptions.slice(SEARCH_TOP_RESULTS_COUNT);
      const restByGroup = new Map<string, MentionOption<T>[]>();
      for (const option of rest) {
        const gid = option.group ?? ungroupedKey;
        const arr = restByGroup.get(gid) ?? [];
        arr.push(option);
        restByGroup.set(gid, arr);
      }
      const orderedGroupIds = orderedGroupIdsFromMap(restByGroup);

      const result: GroupedSuggestionItem<T>[] = [];
      if (topResults.length > 0) {
        for (let i = 0; i < topResults.length; i++) {
          const option = topResults[i]!;
          result.push({
            title: mentionOptionRowTitle(option),
            subtext: option.label,
            context: option,
            onSelect: handleItemSelect,
            groupId: '__suggested__',
            isFirstInGroup: i === 0,
            isLastInGroup: i === topResults.length - 1,
            isFirstInSubgroup: false,
          });
        }
      }

      let isFirstGroup = result.length === 0;
      for (const groupId of orderedGroupIds) {
        const groupOptions = restByGroup.get(groupId) ?? [];
        if (groupOptions.length === 0) {
          continue;
        }
        const groupLabel =
          groupsMapForLabel?.get(groupId) ?? groupId.replace(/-/g, ' ');
        result.push({
          title: groupLabel,
          subtext: groupLabel,
          context: groupOptions[0]!,
          onSelect: noOpSelect,
          groupId,
          isFirstInGroup: isFirstGroup,
          isLastInGroup: true,
          isFirstInSubgroup: false,
          isSubMenuTrigger: true,
          subMenuGroupId: groupId,
          subMenuLabel: groupLabel,
          subMenuOptions: groupOptions,
        });
        isFirstGroup = false;
      }

      return result;
    },
    [
      options,
      groups,
      groupPriority,
      filterOptions,
      handleItemSelect,
      noOpSelect,
      defaultFuzzyFilter,
    ]
  );

  const getSuggestionItems = React.useCallback(
    async (props: { query: string }): Promise<GroupedSuggestionItem<T>[]> =>
      buildSuggestionItems(props.query),
    [buildSuggestionItems]
  );

  const itemsOverrideFn = React.useCallback(
    (query: string): GroupedSuggestionItem<T>[] => buildSuggestionItems(query),
    [buildSuggestionItems]
  );

  const mentionListRef = React.useRef<MentionListHandle>(null);

  return (
    <SuggestionMenu
      char="@"
      allowSpaces={true}
      pluginKey="mentionDropdownMenu"
      decorationClass="tiptap-mention-decoration"
      selector="tiptap-mention-dropdown-menu"
      items={getSuggestionItems}
      itemsOverrideFn={itemsOverrideFn}
      beforeKeyDown={(e) => mentionListRef.current?.handleKeyDown(e) ?? false}
      {...restProps}
    >
      {(props) => (
        <MentionList
          ref={mentionListRef}
          {...props}
          isLoading={isLoading}
          renderOption={renderOption}
          groupsMap={groupsMap}
          subgroupsMap={subgroupsMap}
          handleItemSelect={handleItemSelect}
        />
      )}
    </SuggestionMenu>
  );
};

/**
 * Reveal `el` by scrolling only its nearest scrollable ancestor — unlike native
 * `scrollIntoView`, which walks every ancestor. The menu floats inside the editor
 * and floating-ui positions it async, so on a fresh open its first paint is at the
 * editor's top-left; native scrollIntoView would then yank the whole page up to
 * reveal the auto-selected row.
 */
const scrollSelectedRowIntoView = (el: HTMLElement): void => {
  let scrollParent: HTMLElement | null = el.parentElement;
  while (scrollParent !== null) {
    const overflowY = window.getComputedStyle(scrollParent).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      break;
    }
    scrollParent = scrollParent.parentElement;
  }
  if (scrollParent === null) {
    return;
  }
  const elRect = el.getBoundingClientRect();
  const parentRect = scrollParent.getBoundingClientRect();
  if (elRect.top < parentRect.top) {
    scrollParent.scrollTop -= parentRect.top - elRect.top;
  } else if (elRect.bottom > parentRect.bottom) {
    scrollParent.scrollTop += elRect.bottom - parentRect.bottom;
  }
};

const MentionItem = <T,>({
  item,
  isSelected,
  onSelect,
  renderOption,
  showAvailability = false,
  onHover,
  tall = false,
}: MentionItemProps<T>) => {
  const itemRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (itemRef.current == null || !isSelected) {
      return;
    }
    scrollSelectedRowIntoView(itemRef.current);
  }, [isSelected]);

  const dataWithT = item.context as { data?: { t?: string } } | undefined;
  const color =
    item.context?.color ??
    getResourceMentionColorFromMentionData(dataWithT?.data) ??
    getColorFromMentionType(dataWithT?.data?.t);
  const colorClass = color ? colorVariantClasses[color] : undefined;
  const isAvailable = item.context?.available === true;
  const typeLabel = item.context?.typeLabel;
  const hasTypeLabel =
    !showAvailability && typeLabel !== undefined && typeLabel !== '';

  return (
    <Button
      ref={itemRef}
      variant="ghost"
      data-active-state={isSelected ? 'on' : 'off'}
      onClick={onSelect}
      onMouseEnter={onHover}
      className={cn(
        'tiptap-button !justify-start !bg-transparent hover:!bg-gray-100 data-[active-state=on]:!bg-muted data-[active-state=on]:!text-foreground',
        tall && '!h-10'
      )}
    >
      {renderOption && item.context ? (
        renderOption(item.context)
      ) : (
        <div className="flex w-full min-w-0 items-center gap-2">
          {colorClass && (
            <div
              className={cn('w-2 h-2 rounded-full flex-shrink-0', colorClass)}
            />
          )}
          {hasTypeLabel ? (
            <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
              <span className="tiptap-button-text min-w-0 flex-1 truncate text-left">
                {item.context?.label}
              </span>
              <span className="shrink-0 text-right text-xs text-muted-foreground">
                {typeLabel}
              </span>
            </div>
          ) : (
            <>
              <span className="tiptap-button-text min-w-0 flex-1 truncate text-left">
                {showAvailability
                  ? item.context?.label ?? item.title
                  : item.title}
              </span>
              {showAvailability && isAvailable && (
                <Check
                  className="h-3.5 w-3.5 shrink-0 text-emerald-500"
                  aria-label="Ready to use"
                />
              )}
            </>
          )}
        </div>
      )}
    </Button>
  );
};

const GroupHeader = ({
  label,
  isFirst,
  icon,
}: {
  label: string;
  isFirst: boolean;
  icon?: React.ReactNode;
}) => (
  <div
    className={cn(
      'flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider',
      !isFirst && 'mt-2 border-t border-border pt-2'
    )}
  >
    {icon !== undefined && icon !== null ? (
      <span className="flex shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">{icon}</span>
    ) : null}
    {label}
  </div>
);

/**
 * Plain scroll container that grows with its content up to `maxHeight`, then
 * scrolls natively.
 *
 * Deliberately NOT Radix ScrollArea: Radix wraps children in an inner
 * `display: table` div (so content can exceed the viewport for horizontal
 * scroll). That table sizes to its widest row, which defeats `truncate` on the
 * row labels — long titles grow the column and overflow the type label instead
 * of ellipsizing. A plain block element stays constrained to the column width,
 * so the rows truncate correctly (this is how the menu behaved before the tool
 * discovery redesign introduced the ScrollArea).
 */
const ScrollableArea = ({
  className,
  style,
  children,
}: {
  className?: string;
  /** Pass `{ maxHeight }` here; native overflow scrolls once content exceeds it. */
  style?: React.CSSProperties;
  children: React.ReactNode;
}) => (
  <div
    className={cn('min-h-0 overflow-y-auto subtle-scrollbar', className)}
    style={style}
  >
    {children}
  </div>
);

const SubgroupHeader = ({ label }: { label: string }) => (
  <div className="px-2 py-1 text-xs font-medium text-muted-foreground/80 mt-1.5">
    {label}
  </div>
);

const OverflowIndicator = ({ count }: { count: number }) => (
  <div className="px-2 py-1 text-xs text-muted-foreground italic">
    +{count} more...
  </div>
);

const SuggestedDivider = () => (
  <div className="my-1.5 border-t border-border" role="separator" aria-hidden />
);

const SUBMENU_CLOSE_DELAY_MS = 150;

/**
 * Main mention menu width (also used for single-column submenus). Kept compact so
 * the short category rows (Agents / Documents / Folders / Tools) don't leave a
 * wide empty gap before the chevron, and so the cascade needs less room to open
 * beside it without overlapping. Long names in the flat search list truncate.
 */
const MENTION_MENU_WIDTH_PX = 200;
/** Each column in the two-column Tools cascade submenu. */
const TOOLS_CASCADE_COLUMN_PX = 240;
/**
 * The Tools cascade detail column (plain-language preview of the active tool).
 * Kept identical to the other cascade columns so every block in the cascade is the
 * same size — this makes it much harder to lose the cursor / slip into dead space
 * between columns while navigating (providers → tools → detail).
 */
const TOOLS_CASCADE_DETAIL_COLUMN_PX = TOOLS_CASCADE_COLUMN_PX;
/** Gap between the cascade columns; matches the `gap-1` (4px) flex gap. */
const TOOLS_CASCADE_GAP_PX = 4;
/** Margin reserved for the menu's offset from the viewport edge + collision padding. */
const TOOLS_CASCADE_EDGE_MARGIN_PX = 60;
/**
 * The detail column adds a fourth column, so it only renders when the viewport can
 * actually hold the main menu + providers + tools + detail side by side; otherwise
 * the reserved panel shifts back over the main menu (the overlap we're avoiding).
 * Derived from the real column widths — NOT a magic 900/1100 — so it stays correct
 * if a column width changes. At ~200+240+240+240+gaps+margin this lands near 990; a
 * narrower viewport can't fit four columns, so the preview needs a wider window and
 * falls back to providers + tools below this.
 */
const TOOLS_CASCADE_DETAIL_MIN_VIEWPORT_PX =
  MENTION_MENU_WIDTH_PX +
  TOOLS_CASCADE_GAP_PX +
  TOOLS_CASCADE_COLUMN_PX +
  TOOLS_CASCADE_GAP_PX +
  TOOLS_CASCADE_COLUMN_PX +
  TOOLS_CASCADE_GAP_PX +
  TOOLS_CASCADE_DETAIL_COLUMN_PX +
  TOOLS_CASCADE_EDGE_MARGIN_PX;
/**
 * Below this viewport width the two-column Tools cascade (providers + tools side
 * by side) can't sit beside the main menu, so it collapses to a single drill-down
 * column: providers first, then the selected provider's tools.
 */
const TOOLS_CASCADE_TWO_COLUMN_MIN_VIEWPORT_PX = 760;

const ToolSectionTriggerRow = ({
  label,
  icon,
  isSelected,
  onHover,
  onActivate,
  cancelClose,
}: {
  label: string;
  icon?: React.ReactNode;
  isSelected: boolean;
  onHover: () => void;
  /** Drill into this provider's tools (click / tap). */
  onActivate?: () => void;
  cancelClose: () => void;
}) => {
  const rowRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (rowRef.current == null || !isSelected) {
      return;
    }
    // Keep the selected provider visible as you arrow through the list.
    scrollSelectedRowIntoView(rowRef.current);
  }, [isSelected]);

  return (
    <Button
      ref={rowRef}
      type="button"
      variant="ghost"
      data-active-state={isSelected ? 'on' : 'off'}
      className="tiptap-button !justify-between !bg-transparent hover:!bg-gray-100 data-[active-state=on]:!bg-muted data-[active-state=on]:!text-foreground !h-10 w-full px-2"
      onPointerDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onActivate?.();
      }}
      onMouseEnter={() => {
        cancelClose();
        onHover();
      }}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {icon !== undefined && icon !== null ? (
          <span className="flex shrink-0 [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
        ) : null}
        <span className="tiptap-button-text truncate text-left">{label}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </Button>
  );
};

/**
 * Plain-language preview of a tool, shown in the Tools cascade detail column.
 * Surfaces what the tool does, whether it reads or writes, and what it needs /
 * returns — so users can discover the right tool without reading raw API schemas.
 */
const ToolDetailCard = ({
  detail,
  onOpenDetails,
}: {
  detail: MentionOptionDetail;
  /** Runs the detail's `onOpen` action and dismisses the menu. */
  onOpenDetails?: () => void;
}) => (
  <div className="flex flex-col gap-3 text-left">
    {detail.eyebrow !== undefined && detail.eyebrow !== '' ? (
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {detail.eyebrow}
      </div>
    ) : null}
    <p className="text-sm font-semibold leading-snug text-foreground">
      {detail.summary !== undefined && detail.summary !== ''
        ? detail.summary
        : detail.title}
    </p>
    {detail.access !== undefined ? (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span
          className={cn(
            'h-1.5 w-1.5 shrink-0 rounded-full',
            detail.access.tone === 'write'
              ? 'bg-amber-500'
              : 'bg-mention-purple'
          )}
        />
        <span>{detail.access.label}</span>
      </div>
    ) : null}
    {detail.onOpen !== undefined ? (
      <button
        type="button"
        className="mt-1 inline-flex items-center gap-1 self-start text-xs font-medium text-primary hover:underline"
        // Keep the editor from stealing the click / re-running suggestion logic.
        onPointerDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpenDetails?.();
        }}
      >
        Open details
        <ArrowRight className="h-3.5 w-3.5" />
      </button>
    ) : null}
  </div>
);

const SubmenuTriggerRow = ({
  label,
  icon,
  isSelected,
  onOpen,
  cancelClose,
  itemIndex,
}: {
  label: string;
  icon?: React.ReactNode;
  isSelected: boolean;
  onOpen: () => void;
  cancelClose: () => void;
  itemIndex?: number;
}) => {
  const handleOpen = React.useCallback(() => {
    cancelClose();
    onOpen();
  }, [onOpen, cancelClose]);

  return (
    <Button
      data-item-index={itemIndex}
      variant="ghost"
      data-active-state={isSelected ? 'on' : 'off'}
      className="tiptap-button !justify-between !bg-transparent hover:!bg-gray-100 data-[active-state=on]:!bg-muted data-[active-state=on]:!text-foreground w-full"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleOpen();
      }}
      onPointerDown={(e) => e.preventDefault()}
      onMouseEnter={handleOpen}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {icon !== undefined && icon !== null ? (
          <span className="flex shrink-0 [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
        ) : null}
        <span className="tiptap-button-text truncate text-left">{label}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </Button>
  );
};

type SubmenuState<T> = {
  groupId: string;
  label: string;
  options: MentionOption<T>[];
};

export type MentionListHandle = {
  handleKeyDown: (event: KeyboardEvent) => boolean;
};

const MentionListInner = <T,>(
  {
    items,
    selectedIndex,
    onSelect,
    isLoading = false,
    renderOption,
    groupsMap,
    subgroupsMap,
    handleItemSelect,
    closeMenu,
  }: SuggestionMenuRenderProps<MentionOption<T>> & {
    isLoading?: boolean;
    renderOption?: (option: MentionOption<T>) => React.ReactNode;
    groupsMap?: Map<string, MentionGroup>;
    subgroupsMap?: Map<string, MentionSubgroup>;
    handleItemSelect?: (props: {
      editor: Editor;
      range: Range;
      context?: MentionOption<T>;
    }) => void;
  },
  ref: React.ForwardedRef<MentionListHandle>
) => {
  const [expandedSubmenu, setExpandedSubmenu] =
    React.useState<SubmenuState<T> | null>(null);
  // Miller-column selection for the Tools cascade: which column has keyboard
  // focus ('sections' = providers, 'items' = that provider's tools), plus the
  // active index in each. Up/Down moves within the focused column; Left/Right
  // moves between columns. Non-cascade (single-list) submenus only use
  // `selectedItemIndex` and keep focus on 'items'.
  const [submenuFocus, setSubmenuFocus] = React.useState<'sections' | 'items'>(
    'items'
  );
  const [selectedSectionIndex, setSelectedSectionIndex] = React.useState(0);
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);
  // Tool row the pointer is hovering in the cascade's tools column. Drives the
  // detail column preview when the keyboard focus is still on the providers
  // column. Reset when the active provider changes so we never preview a tool
  // from a different provider.
  const [hoveredItemIndex, setHoveredItemIndex] = React.useState<number | null>(
    null
  );
  // Progressive disclosure for the wide Tools cascade: columns reveal as you
  // drill in. Opening Tools shows only the providers column; engaging a provider
  // (hover / arrow) reveals its tools column; engaging a tool reveals the detail
  // column. `providerActivated` gates the tools column; the detail column is
  // gated by a tool being actively engaged (hover or keyboard focus in 'items').
  const [providerActivated, setProviderActivated] = React.useState(false);
  const submenuCloseTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const cancelSubmenuClose = React.useCallback(() => {
    if (submenuCloseTimeoutRef.current !== null) {
      clearTimeout(submenuCloseTimeoutRef.current);
      submenuCloseTimeoutRef.current = null;
    }
  }, []);

  const scheduleSubmenuClose = React.useCallback(() => {
    cancelSubmenuClose();
    submenuCloseTimeoutRef.current = setTimeout(() => {
      submenuCloseTimeoutRef.current = null;
      setExpandedSubmenu(null);
    }, SUBMENU_CLOSE_DELAY_MS);
  }, [cancelSubmenuClose]);

  const toolSubmenuSections = React.useMemo(() => {
    if (expandedSubmenu?.groupId !== 'tool') {
      return null;
    }
    const toolGroup = groupsMap?.get('tool');
    return buildToolSubmenuSections(
      expandedSubmenu.options,
      toolGroup,
      subgroupsMap
    );
  }, [expandedSubmenu, groupsMap, subgroupsMap]);

  const toolSubmenuFlat = React.useMemo(() => {
    if (toolSubmenuSections === null || toolSubmenuSections.length === 0) {
      return [];
    }
    return flattenToolSubmenuSections(toolSubmenuSections);
  }, [toolSubmenuSections]);

  const isToolsCascadeSubmenu =
    expandedSubmenu !== null &&
    expandedSubmenu.groupId === 'tool' &&
    toolSubmenuSections !== null &&
    toolSubmenuSections.length > 0 &&
    toolSubmenuFlat.length > 0;

  // Show providers + tools side by side only when the viewport is wide enough;
  // otherwise collapse to one column at a time (drill-down).
  const showBothCascadeColumns =
    isToolsCascadeSubmenu &&
    (typeof window === 'undefined' ||
      window.innerWidth >= TOOLS_CASCADE_TWO_COLUMN_MIN_VIEWPORT_PX);

  // The provider section currently in focus, and the tools that belong to it.
  const activeSection = React.useMemo(
    () =>
      isToolsCascadeSubmenu && toolSubmenuSections !== null
        ? toolSubmenuSections[selectedSectionIndex] ??
          toolSubmenuSections[0] ??
          null
        : null,
    [isToolsCascadeSubmenu, toolSubmenuSections, selectedSectionIndex]
  );
  const activeSectionItems = React.useMemo<MentionOption<T>[]>(
    () =>
      isToolsCascadeSubmenu
        ? activeSection?.options ?? []
        : expandedSubmenu?.options ?? [],
    [isToolsCascadeSubmenu, activeSection, expandedSubmenu]
  );

  // A tool is "engaged" once the pointer hovers a tool row or the keyboard has
  // drilled into the tools column. This gates the detail column so it appears
  // only after the user picks a specific tool (not by default).
  const toolActivated =
    isToolsCascadeSubmenu &&
    (submenuFocus === 'items' || hoveredItemIndex !== null);

  // The tool the detail column previews: the keyboard-focused tool when we've
  // drilled into the tools column, otherwise the hovered tool.
  const activeToolItemIndex =
    submenuFocus === 'items' ? selectedItemIndex : hoveredItemIndex ?? 0;
  const activeToolOption =
    isToolsCascadeSubmenu && toolActivated
      ? activeSectionItems[activeToolItemIndex] ?? null
      : null;

  const isWideViewport =
    typeof window === 'undefined' ||
    window.innerWidth >= TOOLS_CASCADE_DETAIL_MIN_VIEWPORT_PX;

  // Progressive disclosure (wide screens): the tools column reveals once a
  // provider is engaged, the detail column once a tool is engaged. On narrow
  // screens the cascade is a single drill-down column, handled separately below.
  const showToolsColumn =
    isToolsCascadeSubmenu && showBothCascadeColumns && providerActivated;
  // The detail column adds a third column, so it needs extra width. Only show it
  // on wide viewports and only when the active tool actually has a preview.
  const showDetailColumn =
    showToolsColumn && isWideViewport && activeToolOption?.detail !== undefined;

  // Each cascade column is narrower when both are shown; full width when alone.
  const cascadeColumnWidthPx = showBothCascadeColumns
    ? TOOLS_CASCADE_COLUMN_PX
    : MENTION_MENU_WIDTH_PX;

  React.useEffect(() => () => cancelSubmenuClose(), [cancelSubmenuClose]);

  const handleSubmenuOptionSelect = React.useCallback(
    (option: MentionOption<T>) => {
      if (handleItemSelect === undefined) {
        return;
      }
      const syntheticItem: GroupedSuggestionItem<T> = {
        title: mentionOptionRowTitle(option),
        subtext: option.label,
        context: option,
        onSelect: handleItemSelect,
      };
      onSelect(syntheticItem);
    },
    [handleItemSelect, onSelect]
  );

  // All keyboard handling routes through here (via SuggestionMenu's `beforeKeyDown`,
  // a capture listener on the editor). Returning true consumes the event so the
  // main-list navigation doesn't also act on it. Because the launcher and cascade
  // now render in one container (no focus-stealing portal), we drive the cascade's
  // column navigation from here too, keyed on `expandedSubmenu`.
  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent): boolean => {
      // --- Cascade open: navigate its columns ---
      if (expandedSubmenu !== null) {
        switch (event.key) {
          case 'Escape': {
            event.preventDefault();
            event.stopPropagation();
            setExpandedSubmenu(null);
            return true;
          }
          case 'ArrowLeft': {
            event.preventDefault();
            event.stopPropagation();
            // From the tools column, step back to the providers column rather than
            // closing the whole cascade. Clear the hovered tool so the detail
            // preview doesn't linger on a tool that's no longer highlighted.
            if (isToolsCascadeSubmenu && submenuFocus === 'items') {
              setHoveredItemIndex(null);
              setSubmenuFocus('sections');
              return true;
            }
            // Leftmost column: collapse the cascade back to the launcher.
            setExpandedSubmenu(null);
            return true;
          }
          case 'ArrowRight': {
            event.preventDefault();
            event.stopPropagation();
            // From the providers column, step into that provider's tools — landing
            // on the tool the pointer is previewing (if any), not always the first.
            if (
              isToolsCascadeSubmenu &&
              submenuFocus === 'sections' &&
              activeSectionItems.length > 0
            ) {
              setProviderActivated(true);
              setSelectedItemIndex(hoveredItemIndex ?? 0);
              setSubmenuFocus('items');
            }
            return true;
          }
          case 'ArrowDown':
          case 'ArrowUp': {
            event.preventDefault();
            event.stopPropagation();
            const delta = event.key === 'ArrowDown' ? 1 : -1;
            if (isToolsCascadeSubmenu && submenuFocus === 'sections') {
              const count = toolSubmenuSections?.length ?? 0;
              if (count === 0) {
                return true;
              }
              // Navigating providers reveals the tools column for the highlighted
              // one. Clear the hovered tool synchronously so the detail preview
              // never shows a stale tool from the previous provider for a frame.
              setHoveredItemIndex(null);
              // The first arrow just activates the (unshaded) column and lands on
              // the current provider; subsequent arrows move the selection.
              if (providerActivated) {
                setSelectedSectionIndex((i) => (i + delta + count) % count);
              } else {
                setProviderActivated(true);
              }
              setSelectedItemIndex(0);
              return true;
            }
            const count = activeSectionItems.length;
            if (count === 0) {
              return true;
            }
            setSelectedItemIndex((i) => (i + delta + count) % count);
            return true;
          }
          case 'Enter': {
            event.preventDefault();
            event.stopPropagation();
            // Enter on a provider drills into its tools (landing on the previewed
            // tool if the pointer is hovering one); Enter on a tool selects it.
            if (isToolsCascadeSubmenu && submenuFocus === 'sections') {
              if (activeSectionItems.length > 0) {
                setProviderActivated(true);
                setSelectedItemIndex(hoveredItemIndex ?? 0);
                setSubmenuFocus('items');
              }
              return true;
            }
            const option = activeSectionItems[selectedItemIndex];
            if (option !== undefined) {
              handleSubmenuOptionSelect(option);
              setExpandedSubmenu(null);
            }
            return true;
          }
          default:
            return false;
        }
      }

      // --- Cascade closed: ArrowRight / Enter on a submenu-trigger row opens it ---
      if (event.key !== 'ArrowRight' && event.key !== 'Enter') {
        return false;
      }
      const idx = selectedIndex ?? -1;
      if (idx < 0 || idx >= items.length) {
        return false;
      }
      const item = items[idx] as GroupedSuggestionItem<T> | undefined;
      if (
        item?.isSubMenuTrigger !== true ||
        item.subMenuOptions === undefined
      ) {
        return false;
      }
      event.preventDefault();
      event.stopPropagation();
      cancelSubmenuClose();
      setExpandedSubmenu({
        groupId: item.subMenuGroupId ?? '',
        label: item.subMenuLabel ?? item.title,
        options: item.subMenuOptions,
      });
      return true;
    },
    [
      expandedSubmenu,
      isToolsCascadeSubmenu,
      submenuFocus,
      activeSectionItems,
      selectedItemIndex,
      hoveredItemIndex,
      providerActivated,
      toolSubmenuSections,
      handleSubmenuOptionSelect,
      selectedIndex,
      items,
      cancelSubmenuClose,
    ]
  );

  React.useImperativeHandle(ref, () => ({ handleKeyDown }), [handleKeyDown]);

  // Run a tool's "Open details" action (e.g. open its connections modal) and tear
  // the menu down first: close the submenu panel, then end the @ suggestion
  // session (removes the in-progress trigger text + decoration and hides the
  // popup) so nothing is left dangling behind the modal.
  const handleOpenToolDetails = React.useCallback(
    (option: MentionOption<T> | null) => {
      const onOpen = option?.detail?.onOpen;
      if (onOpen === undefined) {
        return;
      }
      setExpandedSubmenu(null);
      closeMenu?.();
      onOpen();
    },
    [closeMenu]
  );

  React.useEffect(() => {
    if (expandedSubmenu === null) {
      return;
    }
    setSelectedSectionIndex(0);
    setSelectedItemIndex(0);
    setHoveredItemIndex(null);
    // Start with only the providers column; the tools/detail columns reveal as
    // the user drills in.
    setProviderActivated(false);
    // Tools open on the providers column; everything else is a single list.
    setSubmenuFocus(isToolsCascadeSubmenu ? 'sections' : 'items');
  }, [expandedSubmenu, isToolsCascadeSubmenu]);

  // Never preview a tool from a provider that is no longer active.
  React.useEffect(() => {
    setHoveredItemIndex(null);
  }, [selectedSectionIndex]);

  const renderedItems = React.useMemo(() => {
    const rendered: React.ReactElement[] = [];
    const groupedItems = items as GroupedSuggestionItem<T>[];
    let isFirstGroup = true;

    groupedItems.forEach((item, index) => {
      // Divider between suggested and grouped items (only when there are suggested items)
      const prevItem = index > 0 ? groupedItems[index - 1] : undefined;
      if (
        prevItem?.groupId === '__suggested__' &&
        item.groupId !== '__suggested__'
      ) {
        rendered.push(<SuggestedDivider key="divider-after-suggested" />);
      }

      // Render group header only for inline groups (not for submenu triggers; the trigger row is the group header)
      if (
        item.isFirstInGroup &&
        item.groupId !== undefined &&
        item.isSubMenuTrigger !== true
      ) {
        const label =
          item.groupId === '__suggested__'
            ? 'Suggested'
            : groupsMap?.get(item.groupId)?.label;
        const groupIcon =
          item.groupId !== '__suggested__'
            ? groupsMap?.get(item.groupId)?.icon
            : undefined;
        if (label !== undefined) {
          rendered.push(
            <GroupHeader
              key={`group-${item.groupId}`}
              label={label}
              isFirst={isFirstGroup}
              icon={groupIcon}
            />
          );
          isFirstGroup = false;
        }
      }

      // Render subgroup header if this is the first item in a subgroup
      if (item.isFirstInSubgroup && item.subgroupId && subgroupsMap) {
        const subgroup = subgroupsMap.get(item.subgroupId);
        if (subgroup) {
          rendered.push(
            <SubgroupHeader
              key={`subgroup-${item.groupId}-${item.subgroupId}`}
              label={subgroup.label}
            />
          );
        }
      }

      if (item.isSubMenuTrigger === true && item.subMenuOptions !== undefined) {
        const subMenuOptions = item.subMenuOptions;
        rendered.push(
          <SubmenuTriggerRow
            key={`submenu-${item.subMenuGroupId}`}
            label={item.subMenuLabel ?? item.title}
            icon={groupsMap?.get(item.subMenuGroupId ?? '')?.icon}
            // Once a submenu is open, highlight ONLY its trigger (the open
            // category) — not the keyboard default (index 0), which otherwise
            // stays shaded alongside it and looks like two active rows.
            isSelected={
              expandedSubmenu !== null
                ? expandedSubmenu.groupId === item.subMenuGroupId
                : index === selectedIndex
            }
            onOpen={() =>
              setExpandedSubmenu({
                groupId: item.subMenuGroupId ?? '',
                label: item.subMenuLabel ?? item.title,
                options: subMenuOptions,
              })
            }
            cancelClose={cancelSubmenuClose}
            itemIndex={index}
          />
        );
      } else {
        rendered.push(
          <MentionItem
            key={item.context?.label ?? item.title}
            item={item}
            // Don't keep a launcher row shaded while a submenu is open — the open
            // category (its trigger) is the active thing then.
            isSelected={expandedSubmenu === null && index === selectedIndex}
            onSelect={() => onSelect(item)}
            renderOption={renderOption}
          />
        );
      }

      // Render overflow indicator after the last item in a group
      if (
        !!item.isLastInGroup &&
        item.overflowCount !== undefined &&
        item.overflowCount > 0
      ) {
        rendered.push(
          <OverflowIndicator
            key={`overflow-${item.groupId}`}
            count={item.overflowCount}
          />
        );
      }
    });

    return rendered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    items,
    selectedIndex,
    onSelect,
    renderOption,
    groupsMap,
    subgroupsMap,
    expandedSubmenu,
  ]);

  // Shared height for the launcher + cascade columns. Clamped to a fixed 310px, a
  // fraction of the viewport (72vh, so there's always breathing room and it can't
  // run off the screen), and the space SuggestionMenu measured as available.
  const columnHeight =
    'min(310px, 72vh, var(--suggestion-menu-max-height, 310px))';

  // Whether this menu can drill into a submenu/cascade (any category is a submenu
  // trigger). Only then do we give the launcher a FIXED height equal to the cascade
  // columns: that way the menu is already full-height before the cascade opens, so
  // opening it doesn't resize (and thus doesn't reposition/jump) the menu, and the
  // first three columns are automatically top- AND bottom-aligned. A flat list with
  // no drill-in keeps its compact content height.
  const menuIsDrillable = (items as GroupedSuggestionItem<T>[]).some(
    (item) => item.isSubMenuTrigger === true
  );

  // The cascade columns, rendered INLINE as flex siblings of the launcher (not a
  // separate floating portal). The launcher + first three columns all share one
  // fixed `columnHeight`, so they're top- AND bottom-aligned and the menu is
  // already full-height before the cascade opens — opening it never resizes the
  // menu, so it never repositions or jumps. The detail column (4th) is the
  // exception: it sizes to its own content. Columns reveal progressively.
  const cascadeColumns =
    expandedSubmenu === null ? null : isToolsCascadeSubmenu &&
      toolSubmenuSections !== null &&
      toolSubmenuSections.length > 0 ? (
      <>
        {/* Providers column. Shown alongside tools on wide screens; on narrow
            screens it's shown alone until you drill into a provider. */}
        {(showBothCascadeColumns || submenuFocus === 'sections') && (
          <Card
            className="!py-2 shadow-lg border-border min-h-0 shrink-0"
            style={{ width: cascadeColumnWidthPx }}
          >
            <CardContent className="w-full !px-2">
              <ScrollableArea
                className="w-full"
                style={{ height: columnHeight }}
              >
                <div className="flex flex-col gap-0.5">
                  <GroupHeader
                    label={`Providers · ${toolSubmenuSections.length}`}
                    isFirst={true}
                  />
                  {toolSubmenuSections.map((section, sectionIdx) => (
                    <ToolSectionTriggerRow
                      key={section.id}
                      label={section.label}
                      icon={section.icon}
                      // Don't shade the first provider by default — only highlight
                      // once the user actually engages a provider (hover / arrow),
                      // so nothing looks pre-selected the moment the column opens.
                      isSelected={
                        providerActivated && sectionIdx === selectedSectionIndex
                      }
                      cancelClose={cancelSubmenuClose}
                      onHover={() => {
                        // Hovering a provider reveals its tools column. Clear any
                        // hovered-tool index so the detail preview doesn't briefly
                        // show a stale tool from the previous provider.
                        setProviderActivated(true);
                        setHoveredItemIndex(null);
                        setSelectedSectionIndex(sectionIdx);
                        setSelectedItemIndex(0);
                        setSubmenuFocus('sections');
                      }}
                      onActivate={() => {
                        setProviderActivated(true);
                        setHoveredItemIndex(null);
                        setSelectedSectionIndex(sectionIdx);
                        setSelectedItemIndex(0);
                        setSubmenuFocus('items');
                      }}
                    />
                  ))}
                </div>
              </ScrollableArea>
            </CardContent>
          </Card>
        )}
        {/* Tools column for the active provider — revealed once a provider is
            engaged (wide), or when drilled in (narrow). */}
        {(showToolsColumn ||
          (!showBothCascadeColumns && submenuFocus === 'items')) &&
          activeSection !== null && (
            <Card
              className="!py-2 shadow-lg border-border min-h-0 shrink-0"
              style={{ width: cascadeColumnWidthPx }}
            >
              <CardContent className="w-full !px-2">
                <ScrollableArea
                  className="w-full"
                  style={{ height: columnHeight }}
                >
                  <div className="flex flex-col gap-0.5">
                    <GroupHeader
                      label={`${activeSection.label} · ${
                        activeSection.options.length
                      } ${
                        activeSection.options.length === 1 ? 'tool' : 'tools'
                      }`}
                      isFirst={true}
                    />
                    {activeSection.options.map((option, idx) => (
                      <MentionItem
                        key={
                          option.referenceId ?? `${option.label}-${String(idx)}`
                        }
                        item={{
                          title: mentionOptionRowTitle(option),
                          subtext: option.label,
                          context: option,
                          onSelect: handleItemSelect ?? (() => {}),
                        }}
                        isSelected={
                          submenuFocus === 'items' && idx === selectedItemIndex
                        }
                        showAvailability
                        tall
                        onHover={() => setHoveredItemIndex(idx)}
                        onSelect={() => {
                          handleSubmenuOptionSelect(option);
                          setExpandedSubmenu(null);
                        }}
                        renderOption={renderOption}
                      />
                    ))}
                  </div>
                </ScrollableArea>
              </CardContent>
            </Card>
          )}
        {/* Detail column: plain-language preview of the active tool. Sizes to its
            own content (self-start) rather than stretching to the tall providers
            column — you read it, you don't arrow through its rows. */}
        {showDetailColumn && activeToolOption?.detail !== undefined && (
          <Card
            className="relative !py-0 shadow-lg border-border min-h-0 shrink-0 self-start"
            style={{ width: TOOLS_CASCADE_DETAIL_COLUMN_PX }}
          >
            {/* Speech-bubble tail pointing back toward the selected tool. */}
            <div
              aria-hidden
              className="pointer-events-none absolute -left-1.5 top-1/2 -translate-y-1/2"
            >
              <div className="h-3 w-3 rotate-45 border-b border-l border-border bg-card" />
            </div>
            <CardContent className="w-full !p-0">
              <ScrollableArea
                className="w-full"
                style={{ maxHeight: columnHeight }}
              >
                <div className="px-4 py-4">
                  <ToolDetailCard
                    detail={activeToolOption.detail}
                    onOpenDetails={() =>
                      handleOpenToolDetails(activeToolOption)
                    }
                  />
                </div>
              </ScrollableArea>
            </CardContent>
          </Card>
        )}
      </>
    ) : (
      <Card
        className="!py-2 shadow-lg border-border min-h-0 shrink-0"
        style={{ width: MENTION_MENU_WIDTH_PX }}
      >
        <CardContent className="w-full !px-2">
          <ScrollableArea className="w-full" style={{ height: columnHeight }}>
            <div className="flex flex-col gap-0.5">
              {/* Drill-in column header is plain text (e.g. "Agents"); the
                  category icon lives on the launcher, not here. */}
              <GroupHeader label={expandedSubmenu.label} isFirst={true} />
              {expandedSubmenu.options.map((option, idx) => (
                <MentionItem
                  key={option.referenceId ?? `${option.label}-${String(idx)}`}
                  item={{
                    title: mentionOptionRowTitle(option),
                    subtext: option.label,
                    context: option,
                    onSelect: handleItemSelect ?? (() => {}),
                  }}
                  isSelected={selectedItemIndex === idx}
                  tall
                  onSelect={() => {
                    handleSubmenuOptionSelect(option);
                    setExpandedSubmenu(null);
                  }}
                  renderOption={renderOption}
                />
              ))}
            </div>
          </ScrollableArea>
        </CardContent>
      </Card>
    );

  if (!renderedItems.length) {
    if (isLoading) {
      return (
        <Card className="!py-2">
          <CardContent
            className="!px-2 flex flex-col items-center justify-center gap-2 py-6"
            style={{ width: MENTION_MENU_WIDTH_PX }}
          >
            <div
              className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"
              aria-hidden
            />
            <span className="text-xs text-muted-foreground">
              Loading tools...
            </span>
          </CardContent>
        </Card>
      );
    }
    return null;
  }

  // One flex row: launcher + cascade columns, all top-aligned (`items-start`). When
  // the menu can drill in, the launcher takes the same FIXED `columnHeight` as the
  // cascade columns, so it's already full height before the cascade opens — the menu
  // never resizes/repositions (no jump), and the first columns are top- AND
  // bottom-aligned. A flat, non-drillable menu keeps its compact content height.
  // Entering the menu cancels a pending close; leaving it schedules one, so the
  // cascade stays open while the pointer is anywhere inside.
  return (
    <div
      className="flex flex-row items-start gap-1"
      onMouseEnter={cancelSubmenuClose}
      onMouseLeave={scheduleSubmenuClose}
    >
      <Card className="!py-2 shadow-lg border-border shrink-0">
        <CardContent
          className="!px-2"
          style={{ width: MENTION_MENU_WIDTH_PX, maxWidth: '90vw' }}
        >
          <ScrollableArea
            className="w-full"
            style={
              menuIsDrillable
                ? { height: columnHeight }
                : { maxHeight: columnHeight }
            }
          >
            <div className="flex flex-col gap-0.5">{renderedItems}</div>
          </ScrollableArea>
        </CardContent>
      </Card>
      {cascadeColumns}
    </div>
  );
};

const MentionList = React.forwardRef(MentionListInner) as <T>(
  props: SuggestionMenuRenderProps<MentionOption<T>> & {
    isLoading?: boolean;
    renderOption?: (option: MentionOption<T>) => React.ReactNode;
    groupsMap?: Map<string, MentionGroup>;
    subgroupsMap?: Map<string, MentionSubgroup>;
    handleItemSelect?: (props: {
      editor: Editor;
      range: Range;
      context?: MentionOption<T>;
    }) => void;
  } & { ref?: React.Ref<MentionListHandle> }
) => React.ReactElement;
